import type { CaseDocument } from '../../types';

// Relevance score (0-100) stamped on Document.metadata by the relevance scorer.
export function relevanceOf(doc: CaseDocument): number | null {
  const v = (doc.metadata as Record<string, unknown> | undefined)?.relevance_score;
  return typeof v === 'number' ? v : null;
}

// Select item value for "clear the decision" (Radix items can't use '' or null).
export const CLEAR_DECISION = 'clear';

export interface RelevanceGroup<T> {
  key: 'high' | 'mid' | 'low' | 'none';
  label: string;
  hint: string;
  docs: T[];
}

/** Files grouped by relevance like the documents list's relevance sort, highest first. */
export function groupByRelevance(documents: CaseDocument[]): RelevanceGroup<CaseDocument>[] {
  const groups: Record<RelevanceGroup<CaseDocument>['key'], CaseDocument[]> = { high: [], mid: [], low: [], none: [] };
  for (const d of documents) {
    const r = relevanceOf(d);
    if (r === null) groups.none.push(d);
    else if (r >= 80) groups.high.push(d);
    else if (r >= 50) groups.mid.push(d);
    else groups.low.push(d);
  }
  const byScore = (a: CaseDocument, b: CaseDocument) => (relevanceOf(b) ?? 0) - (relevanceOf(a) ?? 0);
  const all: RelevanceGroup<CaseDocument>[] = [
    { key: 'high', label: 'High relevance', hint: '> 80%', docs: groups.high.sort(byScore) },
    { key: 'mid', label: 'Medium relevance', hint: '50–80%', docs: groups.mid.sort(byScore) },
    { key: 'low', label: 'Low relevance', hint: '< 50%', docs: groups.low.sort(byScore) },
    { key: 'none', label: 'Not scored yet', hint: '', docs: groups.none },
  ];
  return all.filter((g) => g.docs.length > 0);
}
